/**
 * Hook pour vérifier les limites de taux (rate limiting)
 * Utilisé pour protéger contre les abus et les attaques par déni de service
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface RateLimitResult {
  allowed: boolean;
  attemptCount?: number;
  maxAttempts?: number;
  remainingAttempts?: number;
  timeUntilResetSeconds?: number;
  message?: string;
}

export interface UseRateLimitReturn {
  checkRateLimit: (actionType: string, maxAttempts?: number) => Promise<RateLimitResult>;
  isChecking: boolean;
  lastResult: RateLimitResult | null;
}

/**
 * Hook pour vérifier les limites de taux avant d'effectuer une action
 * 
 * @example
 * ```tsx
 * const { checkRateLimit, isChecking } = useRateLimit();
 * 
 * const handleLaunchCampaign = async () => {
 *   const result = await checkRateLimit('campaign_launch', 10);
 *   if (!result.allowed) {
 *     // Afficher un message d'erreur
 *     return;
 *   }
 *   // Continuer avec le lancement
 * };
 * ```
 */
export function useRateLimit(): UseRateLimitReturn {
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState<RateLimitResult | null>(null);
  const { toast } = useToast();

  const checkRateLimit = useCallback(
    async (
      actionType: string,
      maxAttempts: number = 10
    ): Promise<RateLimitResult> => {
      setIsChecking(true);

      try {
        // Récupérer l'organization_id de l'utilisateur actuel
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('Utilisateur non authentifié');
        }

        // Récupérer le profil pour obtenir l'organization_id
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();

        if (profileError || !profile?.organization_id) {
          throw new Error('Organisation non trouvée');
        }

        // Appeler la fonction RPC pour vérifier le rate limit
        const { data, error } = await supabase.rpc('check_rate_limit', {
          p_organization_id: profile.organization_id,
          p_action_type: actionType,
          p_max_attempts: maxAttempts,
          p_window_minutes: 1
        });

        if (error) {
          console.error('Erreur lors de la vérification du rate limit:', error);
          
          // En cas d'erreur, on autorise l'action par sécurité (fail open)
          const result: RateLimitResult = {
            allowed: true,
            message: 'Vérification du rate limit échouée, action autorisée'
          };
          setLastResult(result);
          return result;
        }

        const result: RateLimitResult = {
          allowed: data.allowed,
          attemptCount: data.attempt_count,
          maxAttempts: data.max_attempts,
          remainingAttempts: data.remaining_attempts,
          timeUntilResetSeconds: data.time_until_reset_seconds,
          message: data.message
        };

        setLastResult(result);

        // Afficher un toast si la limite est dépassée
        if (!result.allowed && result.message) {
          toast({
            title: 'Limite de taux dépassée',
            description: result.message,
            variant: 'destructive',
          });
        }

        return result;
      } catch (error) {
        console.error('Erreur lors de la vérification du rate limit:', error);
        
        // En cas d'erreur, on autorise l'action par sécurité
        const result: RateLimitResult = {
          allowed: true,
          message: 'Erreur lors de la vérification, action autorisée'
        };
        setLastResult(result);
        return result;
      } finally {
        setIsChecking(false);
      }
    },
    [toast]
  );

  return {
    checkRateLimit,
    isChecking,
    lastResult
  };
}
